/**
 * Central permission catalog.
 *
 * Every protected API route declares one of these keys. The backend is the
 * source of truth; the frontend only uses the same catalog to decide what to
 * render. A permission may be "scoped": the grant then carries a data scope
 * that limits which records the permission applies to.
 *
 *   own        — records the user owns / is assigned to / created
 *   department — records belonging to the user's department(s)
 *   all        — every record in the tenant
 */

export const PERMISSION_SCOPES = ['own', 'department', 'all'] as const;
export type PermissionScope = (typeof PERMISSION_SCOPES)[number];

export const SCOPE_RANK: Record<PermissionScope, number> = { own: 1, department: 2, all: 3 };

export type ModuleKey = 'dashboard' | 'crm' | 'sales' | 'projects' | 'tasks' | 'hrm' | 'payroll' | 'settings' | 'access' | 'audit';

export interface PermissionDefinition {
  key: string;
  module: ModuleKey;
  label: string;
  /** Grant carries a data scope (own / department / all). */
  scoped?: boolean;
  /** Read-only permission (used to build Observer-style roles). */
  readOnly?: boolean;
  /** Grants access to sensitive personal or financial data. */
  sensitive?: boolean;
}

export const PERMISSION_CATALOG = [
  { key: 'dashboard.company', module: 'dashboard', label: 'View business overview dashboard', readOnly: true },

  { key: 'crm.leads.view', module: 'crm', label: 'View leads', scoped: true, readOnly: true },
  { key: 'crm.leads.create', module: 'crm', label: 'Create leads' },
  { key: 'crm.leads.edit', module: 'crm', label: 'Edit leads, log activities and follow-ups', scoped: true },
  { key: 'crm.leads.delete', module: 'crm', label: 'Delete leads', scoped: true },
  { key: 'crm.leads.assign', module: 'crm', label: 'Assign / reassign leads' },
  { key: 'crm.leads.convert', module: 'crm', label: 'Convert leads to clients' },
  { key: 'crm.leads.export', module: 'crm', label: 'Export leads', readOnly: true },
  { key: 'crm.clients.view', module: 'crm', label: 'View clients', scoped: true, readOnly: true },
  { key: 'crm.clients.create', module: 'crm', label: 'Create clients' },
  { key: 'crm.clients.edit', module: 'crm', label: 'Edit clients', scoped: true },
  { key: 'crm.clients.delete', module: 'crm', label: 'Delete clients', scoped: true },
  { key: 'crm.clients.portal', module: 'crm', label: 'Manage client-portal access and answer client messages / requests', scoped: true },
  { key: 'crm.leads.reopen_conversion', module: 'crm', label: 'Reopen / correct a lead conversion (voids the project and its sales credit)' },

  { key: 'sales.quotations.view', module: 'sales', label: 'View quotations', scoped: true, readOnly: true },
  { key: 'sales.quotations.create', module: 'sales', label: 'Create quotations' },
  { key: 'sales.quotations.edit', module: 'sales', label: 'Edit, send and record client decisions on quotations', scoped: true },
  { key: 'sales.quotations.approve', module: 'sales', label: 'Approve quotations before they are sent' },
  { key: 'sales.quotations.delete', module: 'sales', label: 'Cancel quotations', scoped: true },
  { key: 'sales.targets.view', module: 'sales', label: 'View revenue targets and achievement', scoped: true, readOnly: true },
  { key: 'sales.targets.manage', module: 'sales', label: 'Set revenue targets and the achievement basis' },
  { key: 'sales.attribution.manage', module: 'sales', label: 'Change revenue attribution and make manual credit adjustments' },
  { key: 'sales.commissions.view', module: 'sales', label: 'View incentives and commissions', scoped: true, readOnly: true, sensitive: true },
  { key: 'sales.commissions.manage', module: 'sales', label: 'Configure commission rules; calculate, approve, pay and reverse commissions', sensitive: true },

  { key: 'projects.view', module: 'projects', label: 'View projects', scoped: true, readOnly: true },
  { key: 'projects.create', module: 'projects', label: 'Create projects' },
  { key: 'projects.edit', module: 'projects', label: 'Post progress updates; edit projects and milestones', scoped: true },
  { key: 'projects.manage_team', module: 'projects', label: 'Assign project managers and team members' },
  { key: 'projects.updates.approve', module: 'projects', label: 'Approve progress updates for the client portal', scoped: true },
  { key: 'projects.financials.view', module: 'projects', label: 'View project value, invoices and payments', scoped: true, readOnly: true },
  { key: 'projects.billing.manage', module: 'projects', label: 'Issue invoices and record client payments / refunds' },

  { key: 'tasks.view', module: 'tasks', label: 'View tasks', scoped: true, readOnly: true },
  { key: 'tasks.create', module: 'tasks', label: 'Create tasks' },
  { key: 'tasks.edit', module: 'tasks', label: 'Edit tasks', scoped: true },
  { key: 'tasks.delete', module: 'tasks', label: 'Delete tasks', scoped: true },
  { key: 'tasks.assign', module: 'tasks', label: 'Assign tasks to other people' },
  { key: 'tasks.approve', module: 'tasks', label: 'Approve / reopen completed tasks' },

  { key: 'hrm.employees.view', module: 'hrm', label: 'View employees', scoped: true, readOnly: true },
  { key: 'hrm.employees.create', module: 'hrm', label: 'Add employees' },
  { key: 'hrm.employees.edit', module: 'hrm', label: 'Edit employees', scoped: true },
  { key: 'hrm.employees.delete', module: 'hrm', label: 'Archive employees' },
  {
    key: 'hrm.employees.view_sensitive',
    module: 'hrm',
    label: 'Reveal sensitive employee data (PAN, Aadhaar, bank)',
    scoped: true,
    sensitive: true,
  },
  { key: 'hrm.org.view', module: 'hrm', label: 'View branches, departments, designations', readOnly: true },
  { key: 'hrm.org.manage', module: 'hrm', label: 'Manage branches, departments, designations' },
  { key: 'hrm.attendance.view', module: 'hrm', label: 'View attendance and working-time records', scoped: true, readOnly: true },
  { key: 'hrm.attendance.manage', module: 'hrm', label: 'Force logout / manage working sessions' },
  { key: 'hrm.leave.view', module: 'hrm', label: 'View leave requests and balances', scoped: true, readOnly: true },
  { key: 'hrm.leave.request', module: 'hrm', label: 'Apply for leave' },
  { key: 'hrm.leave.approve', module: 'hrm', label: 'Approve or reject leave requests', scoped: true },
  { key: 'hrm.leave.manage', module: 'hrm', label: 'Manage leave types and the holiday calendar' },

  { key: 'payroll.salary.view', module: 'payroll', label: 'View salary, payroll entries and payslips', scoped: true, readOnly: true, sensitive: true },
  { key: 'payroll.salary.manage', module: 'payroll', label: 'Set employee salaries (effective-dated revisions)', sensitive: true },
  { key: 'payroll.run', module: 'payroll', label: 'Prepare payroll runs (generate, calculate, submit for approval)', sensitive: true },
  { key: 'payroll.approve', module: 'payroll', label: 'Approve or reject payroll runs (cannot approve a run you prepared)', sensitive: true },
  { key: 'payroll.pay', module: 'payroll', label: 'Record salary payments', sensitive: true },
  { key: 'payroll.settings', module: 'payroll', label: 'Configure salary structures, allowances & deductions, PF rules and payroll settings', sensitive: true },
  { key: 'payroll.advances.request', module: 'payroll', label: 'Request a salary advance or loan for yourself' },
  { key: 'payroll.advances.manage', module: 'payroll', label: 'Approve, reject and disburse salary advances and loans', sensitive: true },

  { key: 'settings.view', module: 'settings', label: 'View company settings', readOnly: true },
  { key: 'settings.manage', module: 'settings', label: 'Change company settings' },

  { key: 'access.users.view', module: 'access', label: 'View user accounts', readOnly: true },
  { key: 'access.users.manage', module: 'access', label: 'Manage user accounts, sessions and role assignment' },
  { key: 'access.roles.view', module: 'access', label: 'View roles and permissions', readOnly: true },
  { key: 'access.roles.manage', module: 'access', label: 'Create and edit roles' },

  { key: 'audit.view', module: 'audit', label: 'View audit logs', readOnly: true },
] as const satisfies readonly PermissionDefinition[];

export type PermissionKey = (typeof PERMISSION_CATALOG)[number]['key'];

export const PERMISSION_KEYS = PERMISSION_CATALOG.map((p) => p.key) as PermissionKey[];

const CATALOG_BY_KEY = new Map<string, PermissionDefinition>(PERMISSION_CATALOG.map((p) => [p.key, p]));

export function getPermissionDefinition(key: string): PermissionDefinition | undefined {
  return CATALOG_BY_KEY.get(key);
}

export function isPermissionKey(key: string): key is PermissionKey {
  return CATALOG_BY_KEY.has(key);
}

export interface PermissionGrant {
  key: PermissionKey;
  scope: PermissionScope;
}

export interface PermissionOverride extends PermissionGrant {
  effect: 'allow' | 'deny';
}

/** key -> effective (highest) scope */
export type EffectivePermissions = Partial<Record<PermissionKey, PermissionScope>>;

/**
 * Combine role grants and user-specific overrides into an effective map.
 * - Role grants are unioned; the widest scope wins.
 * - "allow" overrides add / widen a grant.
 * - "deny" overrides remove the permission entirely (deny beats allow).
 * - Unknown keys are ignored (defensive against stale data).
 */
export function resolvePermissions(input: {
  roleGrants: PermissionGrant[][];
  overrides?: PermissionOverride[];
  allPermissions?: boolean;
}): EffectivePermissions {
  const result: EffectivePermissions = {};

  if (input.allPermissions) {
    for (const def of PERMISSION_CATALOG) result[def.key] = 'all';
    return result;
  }

  const widen = (key: PermissionKey, scope: PermissionScope) => {
    const def = CATALOG_BY_KEY.get(key);
    if (!def) return;
    const effective: PermissionScope = def.scoped ? scope : 'all';
    const current = result[key];
    if (!current || SCOPE_RANK[effective] > SCOPE_RANK[current]) result[key] = effective;
  };

  for (const grants of input.roleGrants) for (const g of grants) widen(g.key, g.scope);
  for (const o of input.overrides ?? []) if (o.effect === 'allow') widen(o.key, o.scope);
  for (const o of input.overrides ?? []) if (o.effect === 'deny') delete result[o.key];

  return result;
}

export function hasPermission(
  perms: EffectivePermissions,
  key: PermissionKey,
  minScope: PermissionScope = 'own',
): boolean {
  const scope = perms[key];
  return !!scope && SCOPE_RANK[scope] >= SCOPE_RANK[minScope];
}

export function permissionScope(perms: EffectivePermissions, key: PermissionKey): PermissionScope | null {
  return perms[key] ?? null;
}

// ---------------------------------------------------------------------------
// System role templates (cloned into every tenant at onboarding)
// ---------------------------------------------------------------------------

export type SystemRoleKey =
  | 'owner'
  | 'admin'
  | 'observer'
  | 'sales_manager'
  | 'sales_executive'
  | 'hr_manager'
  | 'team_manager'
  | 'accounts_manager'
  | 'employee';

export interface RoleTemplate {
  key: SystemRoleKey;
  name: string;
  description: string;
  allPermissions?: boolean;
  grants: PermissionGrant[];
}

const g = (key: PermissionKey, scope: PermissionScope = 'all'): PermissionGrant => ({ key, scope });

const readOnlyGrants: PermissionGrant[] = PERMISSION_CATALOG.filter(
  (p) => 'readOnly' in p && p.readOnly && !('sensitive' in p && p.sensitive),
).map((p) => g(p.key, 'all'));

const selfService: PermissionGrant[] = [
  g('tasks.view', 'own'),
  g('tasks.edit', 'own'),
  g('tasks.create'),
  g('hrm.employees.view', 'own'),
  g('hrm.employees.view_sensitive', 'own'),
  g('hrm.attendance.view', 'own'),
  g('hrm.org.view'),
  // Employees see only their own projects, targets, salary, leave and advances.
  g('projects.view', 'own'),
  g('projects.edit', 'own'),
  g('sales.targets.view', 'own'),
  g('sales.commissions.view', 'own'),
  g('hrm.leave.view', 'own'),
  g('hrm.leave.request'),
  g('payroll.salary.view', 'own'),
  g('payroll.advances.request'),
];

export const ROLE_TEMPLATES: RoleTemplate[] = [
  {
    key: 'owner',
    name: 'Company Owner',
    description: 'Full authority over the company workspace. Cannot be edited or removed.',
    allPermissions: true,
    grants: [],
  },
  {
    key: 'admin',
    name: 'Administrator',
    description: 'Company administrator with access to every enabled module.',
    grants: PERMISSION_CATALOG.map((p) => g(p.key, 'all')),
  },
  {
    key: 'observer',
    name: 'Observer / Auditor',
    description: 'Read-only view of business modules. Cannot create, edit, approve or delete anything.',
    grants: readOnlyGrants,
  },
  {
    key: 'sales_manager',
    name: 'Sales Manager',
    description: 'Manages leads, follow-ups and clients for their department.',
    grants: [
      ...selfService,
      g('dashboard.company'),
      g('crm.leads.view', 'department'),
      g('crm.leads.create'),
      g('crm.leads.edit', 'department'),
      g('crm.leads.assign'),
      g('crm.leads.convert'),
      g('crm.leads.export'),
      g('crm.clients.view', 'department'),
      g('crm.clients.create'),
      g('crm.clients.edit', 'department'),
      g('tasks.view', 'department'),
      g('tasks.edit', 'department'),
      g('tasks.assign'),
      g('tasks.approve'),
      g('hrm.employees.view', 'department'),
      g('hrm.attendance.view', 'department'),
      g('crm.clients.portal', 'department'),
      g('sales.quotations.view', 'department'),
      g('sales.quotations.create'),
      g('sales.quotations.edit', 'department'),
      g('sales.quotations.approve'),
      g('sales.quotations.delete', 'department'),
      g('sales.targets.view', 'department'),
      g('sales.commissions.view', 'department'),
      g('projects.view', 'department'),
      g('projects.create'),
      g('projects.financials.view', 'department'),
      g('hrm.leave.view', 'department'),
      g('hrm.leave.approve', 'department'),
    ],
  },
  {
    key: 'sales_executive',
    name: 'Sales Executive / Telecaller',
    description: 'Works assigned leads and follow-ups.',
    grants: [
      ...selfService,
      g('crm.leads.view', 'own'),
      g('crm.leads.create'),
      g('crm.leads.edit', 'own'),
      g('crm.clients.view', 'own'),
      g('crm.clients.portal', 'own'),
      g('sales.quotations.view', 'own'),
      g('sales.quotations.create'),
      g('sales.quotations.edit', 'own'),
      g('projects.financials.view', 'own'),
    ],
  },
  {
    key: 'hr_manager',
    name: 'HR Manager',
    description: 'Manages employees, organisation structure and attendance.',
    grants: [
      ...selfService,
      g('hrm.employees.view'),
      g('hrm.employees.create'),
      g('hrm.employees.edit'),
      g('hrm.employees.delete'),
      g('hrm.employees.view_sensitive'),
      g('hrm.org.view'),
      g('hrm.org.manage'),
      g('hrm.attendance.view'),
      g('hrm.attendance.manage'),
      g('tasks.assign'),
      g('hrm.leave.view'),
      g('hrm.leave.approve'),
      g('hrm.leave.manage'),
      g('payroll.salary.view'),
      g('payroll.salary.manage'),
      g('payroll.run'),
      g('payroll.settings'),
      g('payroll.advances.manage'),
      g('sales.targets.view'),
    ],
  },
  {
    key: 'team_manager',
    name: 'Team / Project Manager',
    description: 'Assigns and reviews work for their department.',
    grants: [
      ...selfService,
      g('dashboard.company'),
      g('tasks.view', 'department'),
      g('tasks.edit', 'department'),
      g('tasks.delete', 'department'),
      g('tasks.assign'),
      g('tasks.approve'),
      g('hrm.employees.view', 'department'),
      g('hrm.attendance.view', 'department'),
      g('projects.view', 'department'),
      g('projects.edit', 'department'),
      g('projects.manage_team'),
      g('projects.updates.approve', 'department'),
      g('hrm.leave.view', 'department'),
      g('hrm.leave.approve', 'department'),
    ],
  },
  {
    key: 'accounts_manager',
    name: 'Accounts Manager',
    description: 'Runs payroll, records salary and client payments, invoices projects and manages commissions.',
    grants: [
      ...selfService,
      g('dashboard.company'),
      g('crm.clients.view'),
      g('sales.quotations.view'),
      g('sales.targets.view'),
      g('sales.commissions.view'),
      g('sales.commissions.manage'),
      g('projects.view'),
      g('projects.financials.view'),
      g('projects.billing.manage'),
      g('payroll.salary.view'),
      g('payroll.run'),
      g('payroll.pay'),
      g('payroll.advances.manage'),
      g('hrm.employees.view'),
      g('hrm.attendance.view'),
      g('hrm.leave.view'),
    ],
  },
  {
    key: 'employee',
    name: 'Employee',
    description: 'Self-service access: own tasks, profile and attendance.',
    grants: selfService,
  },
];

export function getRoleTemplate(key: SystemRoleKey): RoleTemplate {
  const t = ROLE_TEMPLATES.find((r) => r.key === key);
  if (!t) throw new Error(`Unknown role template ${key}`);
  return t;
}

/**
 * Permission keys introduced by the 2026-11 feature update (projects, quotations, targets,
 * commissions, leave, payroll, client portal). Migration `0002` adds the template grants for
 * exactly these keys to existing workspaces' system roles, leaving every other grant untouched.
 */
export const NEW_PERMISSION_KEYS_2026_11: PermissionKey[] = [
  'crm.clients.portal',
  'crm.leads.reopen_conversion',
  'sales.quotations.view',
  'sales.quotations.create',
  'sales.quotations.edit',
  'sales.quotations.approve',
  'sales.quotations.delete',
  'sales.targets.view',
  'sales.targets.manage',
  'sales.attribution.manage',
  'sales.commissions.view',
  'sales.commissions.manage',
  'projects.view',
  'projects.create',
  'projects.edit',
  'projects.manage_team',
  'projects.updates.approve',
  'projects.financials.view',
  'projects.billing.manage',
  'hrm.leave.view',
  'hrm.leave.request',
  'hrm.leave.approve',
  'hrm.leave.manage',
  'payroll.salary.view',
  'payroll.salary.manage',
  'payroll.run',
  'payroll.approve',
  'payroll.pay',
  'payroll.settings',
  'payroll.advances.request',
  'payroll.advances.manage',
];
